import {
  AndonBoardLayoutItem,
  AndonGridCell
} from "~/features/andon/andon.types";
import {
  calculateAndonGridCellKey,
  formatAndonGridCellCoordinates,
  isTextLayoutItem
} from "~/features/andon/andon.utils";
import { AndonWorkstation } from "~/types/api";

// Minimum pointer movement before mousedown becomes a marquee drag instead of a click.
export const MARQUEE_DRAG_THRESHOLD_PX = 6;

// Returns the layout tile covering a grid cell, if any (tiles can span multiple cells).
export function layoutItemAtCell(
  items: AndonBoardLayoutItem[],
  cell: AndonGridCell
): AndonBoardLayoutItem | undefined {
  return items.find(
    (item) =>
      cell.x >= item.x &&
      cell.x < item.x + (item.w || 1) &&
      cell.y >= item.y &&
      cell.y < item.y + (item.h || 1)
  );
}

// All grid cell keys occupied by a layout tile.
export function cellKeysForLayoutItem(item: {
  x: number;
  y: number;
  w?: number;
  h?: number;
}): Set<string> {
  const keys = new Set<string>();
  const width = item.w || 1;
  const height = item.h || 1;
  for (let dy = 0; dy < height; dy += 1) {
    for (let dx = 0; dx < width; dx += 1) {
      keys.add(calculateAndonGridCellKey({ x: item.x + dx, y: item.y + dy }));
    }
  }
  return keys;
}

// Cell keys tied to the open edit menu (marquee, selected tile, or anchor cell).
export function menuOpenCellKeys({
  selectedCells,
  selectedGridIndex,
  workingLayoutItems,
  anchorCell
}: {
  selectedCells: AndonGridCell[];
  selectedGridIndex: string | null;
  workingLayoutItems: AndonBoardLayoutItem[];
  /** Cell under the open menu's anchor position, when known. */
  anchorCell: AndonGridCell | null;
}): Set<string> {
  if (selectedCells.length > 0) {
    return new Set(selectedCells.map(calculateAndonGridCellKey));
  }
  if (selectedGridIndex) {
    const item = workingLayoutItems.find(
      (layoutItem) => layoutItem.i === selectedGridIndex
    );
    if (item) {
      return cellKeysForLayoutItem(item);
    }
  }
  if (anchorCell) {
    const tile = layoutItemAtCell(workingLayoutItems, anchorCell);
    if (tile) {
      return cellKeysForLayoutItem(tile);
    }
    return new Set([calculateAndonGridCellKey(anchorCell)]);
  }
  return new Set();
}

// True when a popover backdrop click includes pointer coordinates (borders mode).
export function isBackdropClickWithClientPoint(
  enableAndonBorders: boolean,
  reason: string | undefined,
  event: object | React.SyntheticEvent | Event | null | undefined
): event is { clientX: number; clientY: number } {
  return (
    enableAndonBorders &&
    reason === "backdropClick" &&
    !!event &&
    typeof event === "object" &&
    "clientX" in event &&
    "clientY" in event
  );
}

// True when the clicked cell is outside the cells already tied to the open menu.
export function shouldReopenEditMenuForClickedCell(
  openKeys: Set<string>,
  clickedCell: AndonGridCell
): boolean {
  return !openKeys.has(calculateAndonGridCellKey(clickedCell));
}

// True when mousedown on empty board background can start a marquee drag.
export function canStartMarqueeFromMouseDown(
  event: { button: number; target: EventTarget | null },
  {
    editModeEnabled,
    enableAndonBorders
  }: { editModeEnabled: boolean; enableAndonBorders: boolean }
): boolean {
  if (!editModeEnabled || !enableAndonBorders || event.button !== 0) {
    return false;
  }
  const target = event.target as HTMLElement | null;
  if (!target) {
    return false;
  }
  if (target.closest(".react-grid-item")) {
    return false;
  }
  if (
    !target.classList.contains("andon-board") &&
    !target.closest(".andon-board")
  ) {
    return false;
  }
  return true;
}

// True when pointer movement has crossed MARQUEE_DRAG_THRESHOLD_PX.
export function hasExceededMarqueeDragThreshold(
  dx: number,
  dy: number
): boolean {
  return Math.hypot(dx, dy) >= MARQUEE_DRAG_THRESHOLD_PX;
}

// Cells to highlight during marquee preview or multi-cell selection.
export function getSelectionHighlightCells({
  marqueePreviewCells,
  selectedCells
}: {
  marqueePreviewCells: AndonGridCell[];
  selectedCells: AndonGridCell[];
}): AndonGridCell[] {
  if (marqueePreviewCells.length > 0) {
    return marqueePreviewCells;
  }
  if (selectedCells.length > 1) {
    return selectedCells;
  }
  return [];
}

// Primary grid cell for the current selection (single cell, tile origin, or anchor).
export function resolveTargetCell({
  selectedCells,
  selectedGridIndex,
  workingLayoutItems,
  anchorCell
}: {
  selectedCells: AndonGridCell[];
  selectedGridIndex: string | null;
  workingLayoutItems: AndonBoardLayoutItem[];
  anchorCell: AndonGridCell | null;
}): AndonGridCell | null {
  if (selectedCells.length === 1) {
    return selectedCells[0];
  }
  if (selectedCells.length > 1) {
    return null;
  }
  if (selectedGridIndex) {
    const boardLayoutItem = workingLayoutItems.find(
      (item) => item.i === selectedGridIndex
    );
    return boardLayoutItem
      ? { x: boardLayoutItem.x, y: boardLayoutItem.y }
      : null;
  }
  return anchorCell;
}

// Title for the workstation picker from the selected tile or target cell.
export function getPickerSelectionTitle({
  selectedGridIndex,
  workstations,
  targetCell
}: {
  selectedGridIndex: string | null;
  workstations?: AndonWorkstation[];
  targetCell: AndonGridCell | null;
}): string | undefined {
  if (selectedGridIndex) {
    const selectedWorkstation = workstations?.find(
      (candidateWorkstation) =>
        candidateWorkstation.workstationId === selectedGridIndex
    );
    return selectedWorkstation?.deviceId ?? selectedGridIndex;
  }
  if (targetCell) {
    return formatAndonGridCellCoordinates(targetCell);
  }
  return undefined;
}

// True when the selected grid item is a text label tile.
export function isTextLabelSelection(
  selectedGridIndex: string | null,
  workingLayoutItems: AndonBoardLayoutItem[]
): boolean {
  return Boolean(
    selectedGridIndex &&
      workingLayoutItems.some(
        (boardLayoutItem) =>
          boardLayoutItem.i === selectedGridIndex &&
          isTextLayoutItem(boardLayoutItem)
      )
  );
}

// Skips the next grid click after marquee or menu actions that also fire click.
export function createSkipNextGridClickGuard() {
  const skipNextGridClickRef = { current: false };
  const skipNextGridClickTimeoutRef: {
    current: ReturnType<typeof setTimeout> | null;
  } = { current: null };

  const clearSkipNextGridClick = () => {
    skipNextGridClickRef.current = false;
    if (skipNextGridClickTimeoutRef.current != null) {
      clearTimeout(skipNextGridClickTimeoutRef.current);
      skipNextGridClickTimeoutRef.current = null;
    }
  };

  const armSkipNextGridClick = () => {
    skipNextGridClickRef.current = true;
    if (skipNextGridClickTimeoutRef.current != null) {
      clearTimeout(skipNextGridClickTimeoutRef.current);
    }
    skipNextGridClickTimeoutRef.current = setTimeout(() => {
      skipNextGridClickRef.current = false;
      skipNextGridClickTimeoutRef.current = null;
    }, 100);
  };

  return {
    skipNextGridClickRef,
    armSkipNextGridClick,
    clearSkipNextGridClick
  };
}
